import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BusinessUnitBricksService } from '../business-unit-bricks.service';
import { BusinessUnitBricks } from '../../../models/lookups/business-unit-bricks.model';
import { DisplayModes } from '../../../models/enums';
import { AlertService, MessageSeverity, DialogType } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { ProvisionBusinessUnitBricksComponent } from '../provision-business-unit-bricks/provision-business-unit-bricks.component';

type Panel = 'none' | 'add' | 'upload' | 'close' | 'closeMany';

@Component({
  selector: 'crm-manage-business-unit-bricks',
  templateUrl: './manage-business-unit-bricks.component.html',
  styleUrls: ['./manage-business-unit-bricks.component.css']
})
export class ManageBusinessUnitBricksComponent implements OnInit {

  @Input() businessUnitId: number;
  @Input() businessUnitName: string;
  @Input() isSalesBrick: boolean;
  @Output() closeEvent = new EventEmitter<boolean>();

  @ViewChild("provisionBusinessUnitBricks")
  provisionBusinessUnitBricks: ProvisionBusinessUnitBricksComponent;

  links: BusinessUnitBricks[] = [];
  displayMode: DisplayModes = DisplayModes.Loading;
  panel: Panel = 'none';
  showOpen = true;
  showClosed = false;
  search = '';

  // close panel
  closingLink: BusinessUnitBricks;
  closeEndDate = '';
  closeSubmitted = false;

  // close several open links at once (ticked rows)
  selectedIds: number[] = [];

  // inline edit
  editingId: number = null;
  editStart = '';
  editEnd = '';
  saving = false;

  // percent details pop-up (sales bricks)
  share: any = null;
  shareLoading = false;

  constructor(private businessUnitBricksService: BusinessUnitBricksService,
    private alertService: AlertService,
    private translateService: AppTranslationService,
    private route: ActivatedRoute) {
  }

  openShare(link: BusinessUnitBricks) {
    this.share = null;
    this.shareLoading = true;
    this.businessUnitBricksService.getBrickShareDetails(link.id, this.isSalesBrick).subscribe(details => {
      this.shareLoading = false;
      this.share = details;
    }, err => {
      this.shareLoading = false;
      this.alertService.showMessage('', err && err.error && err.error.Message ? err.error.Message : 'Failed to load the percent details.', MessageSeverity.Error);
    });
  }

  closeShare() {
    this.share = null;
    this.shareLoading = false;
  }

  ngOnInit() {
    if (typeof (this.isSalesBrick) == 'string')
      this.isSalesBrick = (this.isSalesBrick as any) == 'true';
    // also opened on its own route: manage-business-unit-bricks/:businessUnitId?sales=true
    if (this.businessUnitId == undefined && this.route.snapshot.params['businessUnitId']) {
      this.businessUnitId = +this.route.snapshot.params['businessUnitId'];
      this.isSalesBrick = this.route.snapshot.queryParams['sales'] == 'true';
    }
    this.load();
  }

  load() {
    if (this.businessUnitId == undefined)
      return;
    this.businessUnitBricksService.getBusinessUnitBricksByBusinessUnitd(this.businessUnitId, this.isSalesBrick).subscribe(response => {
      this.links = response as BusinessUnitBricks[];
      this.displayMode = DisplayModes.Success;
    }, () => this.displayMode = DisplayModes.Error);
  }

  private matches(x: BusinessUnitBricks) {
    const text = (this.search || '').toLowerCase();
    return !text || (x.brickName || '').toLowerCase().indexOf(text) != -1;
  }

  get openLinks() { return this.links.filter(x => !x.isClosed && this.matches(x)); }
  get closedLinks() { return this.links.filter(x => x.isClosed && this.matches(x)); }
  get hasNoCentre() { return !this.isSalesBrick && this.links.some(x => !x.isClosed) && !this.links.some(x => !x.isClosed && x.isCentre); }

  private showError(err, fallback: string) {
    this.alertService.showMessage("", err && err.error && err.error.Message ? err.error.Message : fallback, MessageSeverity.Error);
  }

  private day(value: string) {
    return value ? value.substring(0, 10) : '';
  }

  openPanel(panel: Panel) {
    this.cancelEdit();
    this.panel = this.panel == panel ? 'none' : panel;
    if (this.panel == 'upload' && this.provisionBusinessUnitBricks)
      this.provisionBusinessUnitBricks.reset();
  }

  onAddClosed(saved: boolean) {
    this.panel = 'none';
    if (saved)
      this.load();
  }

  closeUpload() {
    this.panel = 'none';
    this.load();
  }

  // Is Centre switched straight from the table
  toggleCentre(link: BusinessUnitBricks, event) {
    const value = event.target.checked;
    this.businessUnitBricksService.updateBusinessUnitBrick({ id: link.id, startDate: this.day(link.startDate), endDate: this.day(link.endDate), isCentre: value }, this.isSalesBrick)
      .subscribe(() => {
        link.isCentre = value;
        this.load();
      }, err => {
        event.target.checked = !value;
        this.showError(err, "Failed to save.");
      });
  }

  startEdit(link: BusinessUnitBricks) {
    this.panel = 'none';
    this.editingId = link.id;
    this.editStart = this.day(link.startDate);
    this.editEnd = this.day(link.endDate);
  }

  cancelEdit() {
    this.editingId = null;
  }

  saveEdit(link: BusinessUnitBricks) {
    if (!this.editStart) {
      this.alertService.showMessage("", "Start date is required.", MessageSeverity.Error);
      return;
    }
    if (this.editEnd && this.editEnd < this.editStart) {
      this.alertService.showMessage("", "The end date can't be before the start date.", MessageSeverity.Error);
      return;
    }
    this.saving = true;
    this.businessUnitBricksService.updateBusinessUnitBrick({ id: link.id, startDate: this.editStart, endDate: this.editEnd, isCentre: link.isCentre }, this.isSalesBrick)
      .subscribe(() => {
        this.saving = false;
        this.editingId = null;
        this.alertService.showMessage("", this.translateService.getTranslation("Successfully"), MessageSeverity.Success);
        this.load();
      }, err => {
        this.saving = false;
        this.showError(err, "Failed to save.");
      });
  }

  isSelected(link: BusinessUnitBricks) {
    return this.selectedIds.indexOf(link.id) > -1;
  }

  toggleSelected(link: BusinessUnitBricks, checked: boolean) {
    this.selectedIds = this.selectedIds.filter(id => id != link.id);
    if (checked)
      this.selectedIds.push(link.id);
  }

  /** Every open link shown (the search narrows it), so "all" is what the user sees. */
  get allOpenSelected() {
    return this.openLinks.length > 0 && this.openLinks.every(x => this.isSelected(x));
  }

  toggleAllOpen(checked: boolean) {
    const shown = this.openLinks.map(x => x.id);
    this.selectedIds = this.selectedIds.filter(id => shown.indexOf(id) == -1);
    if (checked)
      this.selectedIds = this.selectedIds.concat(shown);
  }

  get selectedLinks() {
    return this.links.filter(x => !x.isClosed && this.isSelected(x));
  }

  /** The end date can't be before any selected link's start. */
  get selectedMinEndDate() {
    return this.selectedLinks.map(x => this.day(x.startDate)).filter(x => !!x).sort().pop() || '';
  }

  startCloseMany() {
    this.cancelEdit();
    this.closingLink = null;
    this.closeEndDate = '';
    this.closeSubmitted = false;
    this.panel = 'closeMany';
  }

  confirmCloseMany() {
    this.closeSubmitted = true;
    const ids = this.selectedLinks.map(x => x.id);
    if (!this.closeEndDate || ids.length == 0)
      return;
    const endDate = this.closeEndDate;
    this.alertService.showDialog(`Close ${ids.length} brick link${ids.length == 1 ? '' : 's'} on ${endDate}?`, DialogType.Confirm, () => {
      this.saving = true;
      this.businessUnitBricksService.closeBusinessUnitBricks(ids, endDate, this.isSalesBrick).subscribe(count => {
        this.saving = false;
        this.panel = 'none';
        this.selectedIds = [];
        this.alertService.showMessage("", `${count} brick link${count == 1 ? ' was' : 's were'} closed.`, MessageSeverity.Success);
        this.load();
      }, err => {
        this.saving = false;
        this.showError(err, "Failed to close the brick links.");
      });
    }, null, "Close links", this.translateService.getTranslation("app.cancel"));
  }

  startClose(link: BusinessUnitBricks) {
    this.cancelEdit();
    this.closingLink = link;
    this.closeEndDate = '';
    this.closeSubmitted = false;
    this.panel = 'close';
  }

  confirmClose() {
    this.closeSubmitted = true;
    if (!this.closeEndDate)
      return;
    this.saving = true;
    this.businessUnitBricksService.deleteBusinessUnitBrickById(this.closingLink.id, this.closeEndDate, this.isSalesBrick).subscribe(() => {
      this.saving = false;
      this.panel = 'none';
      this.alertService.showMessage("", "The brick link was closed.", MessageSeverity.Success);
      this.load();
    }, err => {
      this.saving = false;
      this.showError(err, "Failed to close the brick link.");
    });
  }

  back() {
    this.closeEvent.emit(true);
  }
}
